import { DataSource } from '@angular/cdk/collections';
import { MatPaginator, MatSort } from '@angular/material';
import { map } from 'rxjs/operators';
import { Observable, merge, BehaviorSubject } from 'rxjs';
import { Event } from '../../models/event';
import { isNullOrUndefined } from 'util';
import { TimeFilterUpdate } from './controls/controls.component';


/**
 * Data source for the TableEvents view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
export class TableEventsDataSource extends DataSource<Event[]> {

    // Data is behaviour subject for observing changes
    private _data = new BehaviorSubject<Event[]>([]);

    get data(): Event[] {
        return this._data.value;
    }

    set data(events: Event[]) {
        this._data.next(events);
    }

    get totalCount(): number {
        return this.paginator.length;
    }

    set totalCount(eventCount: number) {
        this.paginator.length = eventCount;
    }

    get sortOrder(): string {
        return this.sort.direction;
    }

    get sortProperty(): string {
        return this.sort.active;
    }

    // Time Filter
    private _filterChange = new BehaviorSubject<TimeFilterUpdate>(null);

    filteredDataLength: number = 0; // need length for paginators in table

    get filter(): TimeFilterUpdate {
        return this._filterChange.value || null;
    }

    set filter(filter: TimeFilterUpdate) {
        this._filterChange.next(filter);
    }

    constructor(private paginator: MatPaginator, private sort: MatSort) {
        super();
    }

    /**
     * Connect this data source to the table. The table will only update when
     * the returned stream emits new items.
     * @returns A stream of the items to be rendered.
     */
    connect(): Observable<any[]> {
        // Combine everything that affects the rendered data into one update
        // stream for the data-table to consume.
        const dataMutations = [
            this.paginator.page,
            this.sort.sortChange,
            this._filterChange,
            this._data
        ];

        // Set the paginators length
        this.paginator.length = this.data.length;

        return merge(...dataMutations).pipe(map(() => {
            return this.getFilteredData([...this.data], this.totalCount)
        }));
    }

    /**
     *  Called when the table is being destroyed. Use this function, to clean up
     * any open connections or free any held resources that were set up during connect.
     */
    disconnect() { }

    // Iterate over every field in the TableGatesItem class,
    // checking for matches with the filter string.
    // Ignores the field names, and only uses the field value (val)
    private filterFunction(item: Event) {
        if (isNullOrUndefined(item)) {
            return false;
        }

        // If filter is null, do not filter
        if (isNullOrUndefined(this.filter)) {
            return true;
        }

        let date = new Date(item.timeStamp);
        let hours = date.getHours();
        let minutes = date.getMinutes();

        // Format to look like "23:59"
        let timeString = `${hours}:${minutes}`;

        // console.log("filter: ", timeString);

        // Check if time is between toTime and fromTime
        if (timeString >= this.filter.fromTime && timeString <= this.filter.toTime) {
            return true;
        }

        // No match
        return false;
    }

    private getFilteredData(data: Event[], totalCount: number) {
        let filteredData = data.filter(this.filterFunction, this);
        this.filteredDataLength = totalCount;
        return filteredData;
    }
}
